import React, { useCallback, useContext  } from "react";
import { BryntumButton } from "@bryntum/schedulerpro-react";
import { EDITOR_KEYS } from "../../../../../other/constants/EditorKeys.jsx";
import {GRANTS} from "../../../../../other/constants/PrivilegeTypes.jsx";
import BryntumContext from "../../../../context/bryntumContext.jsx";
import {SWIVU_STANDARD_ICONS} from "../../../../../other/constants/IconKeys.jsx";
import { ALERT_TYPES, APP_HEADER_HOVER_MENU_BUTTON_CLS, VIEW_TYPES } from "../../../../../other/constants/OtherKeys.jsx";
import {canAddNewUser, openNewSpotIncentiveEditor} from "../../../../../other/utils/utils.jsx";
import { INCENTIVE_PERMISSION_KEYS } from "@/other/constants/IncentiveKeys.jsx";
import { checkIncentivePermission } from "@/other/utils/incentiveUtils.jsx";

const AppAddButton = () => {
  const { user, editorHandler, project } = useContext(BryntumContext);

  const onCreateNewTask = useCallback(() => {
    const newRecord = project.extraTaskStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.taskEditor, newRecord, { task: newRecord });
  }, [editorHandler, project]);

  const onCreateNewResource = useCallback(() => {
    const newRecord = project.resourceStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.resourceEditorV2, newRecord, {resource: newRecord });
  }, [editorHandler, project]);

  const onCreateNewClient = useCallback(() => {
    const newRecord = project.clientsStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.clientEditorV2, newRecord, { client: newRecord });
  }, [editorHandler, project]);

  const onCreateNewUser = useCallback(() => {
    const newRecord = project.boardUsersStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.userEditor, newRecord, { user: newRecord });
  }, [editorHandler, project]);

  const onCreateNewContact = useCallback(() => {
    const newRecord = project.contactsStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.contactFinder, newRecord, { contactRecord: newRecord }, {
      by: "recordType", useExisting: true,
    });
  }, [editorHandler, project]);

  const addNewBoardAlert = useCallback(() => {
    const newRecord = project.reminderStore.createRecord({
      alertType: ALERT_TYPES.BoardAlert
    });
    editorHandler.openEditor(EDITOR_KEYS.boardAlertEditor, null, { reminderRecord: newRecord }, { by: "recordType", useExisting: true });
  }, [editorHandler]);

  const onAddNewBudget = useCallback(() => {
    const newRecord = project.taskBudgetStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.budgetEditor, newRecord, { budget: newRecord });
  }, [editorHandler, project]);

  const onCreateNewOutcomeIncentive = useCallback(() => {
    const newRecord = project.outcomeIncentiveStore.createRecord();
    editorHandler.openEditor(EDITOR_KEYS.outcomeIncentiveEditor, newRecord, { incentiveRecord: newRecord });
  }, [editorHandler, project]);

  const onCreateNewSpotIncentive = useCallback(() => {
    openNewSpotIncentiveEditor({ project, editorHandler });
  }, [editorHandler, project]);

  const canCreate = user && user.can(GRANTS.grant_four);
  const canCreateUser = user && canAddNewUser(user);

  const globalPlusMenu = {
    cls: "swivu-header-menu-rounded",
    itemCls: "swivu-header-menu-item",
    listeners: {
      beforeShow: ({ source }) => {
        const hasManagePermission = checkIncentivePermission({ project, permissionKey: INCENTIVE_PERMISSION_KEYS.INCENTIVE_MANAGERS });
        const { outcomeIncentive, spotIncentive } = source.widgetMap.newIncentive.menu.widgetMap;
        outcomeIncentive.disabled = !hasManagePermission;
        spotIncentive.disabled = !hasManagePermission;
      }
    },
    items: {
      newResource: {
        text: "New Resource",
        icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.resources],
        onItem: onCreateNewResource,
        disabled: !canCreate,
      },
      newTask: {
        text: "New Task",
        icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.tasks],
        onItem: onCreateNewTask,
        disabled: !canCreate
      },
      newClient: {
        text: "New Client",
        icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.clients],
        onItem: onCreateNewClient,
        disabled: !canCreate
      },
      newUser: {
        text: "New User",
        icon: SWIVU_STANDARD_ICONS.addUser,
        onItem: onCreateNewUser,
        disabled: canCreateUser
      },
      newContact: {
        text: "New Contact",
        icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.contacts],
        onItem: onCreateNewContact,
        disabled: !canCreate
      },
      newAlert: {
        text: "New Alert",
        icon: "fa-regular fa-message-plus",
        disabled: (user && !user.can(GRANTS.grant_three)) || (user && user.isDemoUser && user.isSandboxBoard),
        onItem: addNewBoardAlert
      },
      newBudget: {
        text: "New Budget",
        icon: "fa-light fa-coins",
        disabled: (user && (user.isSandboxBoard && user.isDemoUser || !user.can(GRANTS.grant_two))),
        onItem: onAddNewBudget
      },
      newIncentive: {
        text: "New Incentive",
        icon: SWIVU_STANDARD_ICONS[VIEW_TYPES.incentivePoints],
        menu: {
          cls: "swivu-header-menu-rounded",
          itemCls: "swivu-header-menu-item",
          items: {
            outcomeIncentive: {
              text: "Outcome",
              onItem: onCreateNewOutcomeIncentive,
            },
            spotIncentive: {
              text: "Spot",
              onItem: onCreateNewSpotIncentive,
            }
          }
        }
      }
    }
  };

  return <BryntumButton
    cls={`b-transparent !bg-[#CCCCCC] dark:!bg-[#242429] text-[#0c0a09] dark:text-white min-h-[auto] h-[2.5em] w-[2.5em] p-0 !rounded-md ${APP_HEADER_HOVER_MENU_BUTTON_CLS}`}
    icon="fa-solid fa-plus"
    menuIcon={null}
    menu={globalPlusMenu}
  />
};

export default AppAddButton;
